export class ApiClientError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'ApiClientError';
    this.status = details.status ?? null;
    this.method = details.method ?? null;
    this.path = details.path ?? null;
    this.payload = details.payload ?? null;
    this.authCode = details.authCode ?? null;
    this.apiCode = details.apiCode ?? null;
    this.cause = details.cause;
  }
}

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const hasHeader = (headers, name) =>
  Object.keys(headers || {}).some((key) => key.toLowerCase() === name.toLowerCase());

/** Read the additive auth error code without depending on display text. */
export const readAuthErrorCode = (response, payload) => {
  if (!response || response.ok || response.status < 400) return null;
  const code = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? payload.code
    : null;
  const value = response.headers?.get?.('X-Auth-Error-Code') || code;
  return typeof value === 'string' && /^AUTH_[A-Z_]+$/.test(value) ? value : null;
};

/** Read a structured API error code without parsing display text. */
export const readApiErrorCode = (response, payload) => {
  if (!response || response.ok || response.status < 400) return null;
  const code = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload.code : null;
  const value = response.headers?.get?.('X-Api-Error-Code') || code;
  return typeof value === 'string' && /^[A-Z][A-Z0-9_]+$/.test(value) ? value : null;
};

const isAbsoluteUrl = (path) => /^https?:\/\//i.test(path);

const defaultBuildQueryString = (params = {}) => {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null);
  if (!entries.length) return '';
  const query = new URLSearchParams(entries).toString();
  return query ? `?${query}` : '';
};

export const buildQueryString = (params = {}, options = {}) => {
  const { omitEmptyString = false } = options;
  const entries = Object.entries(params).filter(([, value]) => (
    value !== undefined &&
    value !== null &&
    (!omitEmptyString || value !== '')
  ));
  if (!entries.length) return '';
  const query = new URLSearchParams(entries).toString();
  return query ? `?${query}` : '';
};

const normalizeDataPayload = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return payload;
  }
  return hasOwn(payload, 'data') ? payload.data : payload;
};

const parseBodySafely = async (response) => {
  // A failed response.json() consumes the stream, so read it just once and
  // retain non-JSON errors (including the gateway's missing-route response).
  let raw;
  try {
    raw = await response.text();
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const resolveErrorMessage = (response, payload) => {
  if (payload && typeof payload === 'object') {
    if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
    if (Array.isArray(payload.message) && payload.message.length) return payload.message.join(', ');
    if (typeof payload.error === 'string' && payload.error.trim()) return payload.error;
    if (payload.error && typeof payload.error.message === 'string' && payload.error.message.trim()) {
      return payload.error.message;
    }
  }
  if (typeof payload === 'string' && payload.trim()) return payload;
  return response.statusText || 'Request failed';
};

const serializeBody = (body) => {
  if (body === undefined || body === null) return null;
  if (typeof body === 'string') return body;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return body;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return body;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return body;
  return JSON.stringify(body);
};

const defaultIsExpiredAuthResponse = (response, payload) => {
  if (response?.status !== 401) return false;
  const code = readAuthErrorCode(response, payload);
  if (code) return code === 'AUTH_TOKEN_EXPIRED';
  const message = String(payload?.message || payload?.error || payload || '').toLowerCase();
  return message.includes('token has expired') ||
    message.includes('jwt expired') ||
    message.includes('session expired') ||
    message.includes('invalid or expired token');
};

export const createApiClient = ({
  baseUrl = '',
  resolveAuthToken = () => null,
  buildQuery = defaultBuildQueryString,
  defaultContract = 'payload',
  onMetricStart = null,
  onMetricEnd = null,
  onMissingAuthTokenError = null,
  refreshSession = null,
  expireSession = null,
  isExpiredAuthResponse = defaultIsExpiredAuthResponse,
  jsonContentTypeRequiresBody = true,
} = {}) => {
  const request = async (path, options = {}) => {
    const {
      method = 'GET',
      params,
      body,
      headers = {},
      token,
      auth = true,
      jsonContentType = true,
      signal,
      contract = defaultContract,
      throwOnError = true,
      cacheAdapter,
      skipMetrics = false,
      skipAuthRefresh = false,
    } = options;

    const methodUpper = String(method || 'GET').toUpperCase();
    const query = buildQuery(params || {});
    const requestPath = `${path}${query}`;
    const targetUrl = isAbsoluteUrl(requestPath) ? requestPath : `${baseUrl}${requestPath}`;
    const authToken = auth ? resolveAuthToken(token) : null;
    const preparedHeaders = { ...headers };
    const serializedBody = serializeBody(body);
    const isFormDataBody = typeof FormData !== 'undefined' && serializedBody instanceof FormData;

    if (authToken && !hasHeader(preparedHeaders, 'Authorization')) {
      preparedHeaders.Authorization = `Bearer ${authToken}`;
    }

    if (
      jsonContentType &&
      (!jsonContentTypeRequiresBody || serializedBody !== null) &&
      !isFormDataBody &&
      !hasHeader(preparedHeaders, 'Content-Type')
    ) {
      preparedHeaders['Content-Type'] = 'application/json';
    }

    const metric = !skipMetrics && !cacheAdapter && onMetricStart
      ? onMetricStart({ path: requestPath, method: methodUpper })
      : null;

    const execute = async ({ retryToken = authToken } = {}) => {
      let response;
      let payload;

      const retryHeaders = { ...preparedHeaders };
      if (retryToken && auth && !hasHeader(headers, 'Authorization')) {
        retryHeaders.Authorization = `Bearer ${retryToken}`;
      }

      try {
        response = await fetch(targetUrl, {
          method: methodUpper,
          headers: retryHeaders,
          ...(serializedBody !== null ? { body: serializedBody } : {}),
          ...(signal ? { signal } : {}),
        });

        const parseTarget = contract === 'raw' ? response.clone() : response.clone?.() || response;
        payload = await parseBodySafely(parseTarget);
      } catch (error) {
        if (metric && onMetricEnd) {
          onMetricEnd(metric, { status: error?.status ?? null, error });
        }
        throw new ApiClientError(error?.message || 'Network request failed', {
          method: methodUpper,
          path: requestPath,
          cause: error,
        });
      }

      if (metric && onMetricEnd) {
        onMetricEnd(metric, { status: response.status, payload });
      }

      return { response, payload };
    };

    if (cacheAdapter && methodUpper === 'GET') {
      if (contract === 'raw') {
        throw new ApiClientError('cacheAdapter does not support "raw" contract mode.', {
          method: methodUpper,
          path: requestPath,
        });
      }

      const cachedPayload = await cacheAdapter({
        path: requestPath,
        method: methodUpper,
        headers: preparedHeaders,
        token: authToken,
        signal,
        body: serializedBody,
        request: async () => {
          const result = await execute();
          if (!result.response.ok && throwOnError) {
            throw new ApiClientError(resolveErrorMessage(result.response, result.payload), {
              status: result.response.status,
              apiCode: readApiErrorCode(result.response, result.payload),
              method: methodUpper,
              path: requestPath,
              payload: result.payload,
            });
          }
          return result.payload;
        },
      });

      return contract === 'data' ? normalizeDataPayload(cachedPayload) : cachedPayload;
    }

    const { response, payload } = await execute();

    if (auth && refreshSession && !skipAuthRefresh && isExpiredAuthResponse(response, payload)) {
      try {
        const refreshedSession = await refreshSession();
        const retry = await request(path, {
          ...options,
          token: refreshedSession?.token || refreshedSession?.accessToken,
          skipAuthRefresh: true,
        });
        return retry;
      } catch (refreshError) {
        if (expireSession) {
          expireSession(refreshError?.message || 'Session expired');
        }
        if (throwOnError) {
          throw new ApiClientError('Session expired', {
            status: response.status,
            authCode: readAuthErrorCode(response, payload),
            apiCode: readApiErrorCode(response, payload),
            method: methodUpper,
            path: requestPath,
            payload,
            cause: refreshError,
          });
        }
      }
    }

    if (!response.ok && throwOnError) {
      const message = resolveErrorMessage(response, payload);
      if (onMissingAuthTokenError) {
        onMissingAuthTokenError({
          response,
          authToken,
          message,
          method: methodUpper,
          path: requestPath,
        });
      }
      throw new ApiClientError(message, {
        status: response.status,
        authCode: readAuthErrorCode(response, payload),
        apiCode: readApiErrorCode(response, payload),
        method: methodUpper,
        path: requestPath,
        payload,
      });
    }

    const result = {
      status: response.status,
      payload,
      response,
    };

    if (contract === 'raw') return result;
    if (contract === 'result') return result;
    if (contract === 'data') return normalizeDataPayload(payload);
    return payload;
  };

  const get = (path, options = {}) => request(path, { ...options, method: 'GET' });
  const post = (path, body, options = {}) => request(path, { ...options, method: 'POST', body });
  const put = (path, body, options = {}) => request(path, { ...options, method: 'PUT', body });
  const patch = (path, body, options = {}) => request(path, { ...options, method: 'PATCH', body });
  const remove = (path, options = {}) => request(path, { ...options, method: 'DELETE' });

  return {
    request,
    get,
    post,
    put,
    patch,
    delete: remove,
    del: remove,
  };
};
